package com.fantasytenis.web.components

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.domain.auth.entity.UserEntity
import com.fantasytenis.shared.domain.engagement.entity.EngagementEntity
import com.fantasytenis.shared.domain.wallet.entity.WalletEntity
import com.fantasytenis.web.hooks.rememberWindowWidth
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.Theme
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Responsive header component that adapts to screen size.
 *
 * On desktop (>= 768px): Shows full header with all actions inline.
 * On mobile (< 768px): Shows title/back button + hamburger menu that opens a dropdown.
 *
 * @param title The header title to display
 * @param showBackButton Whether to show a back button
 * @param onBackClick Callback when back button is clicked
 * @param showUserMenu Whether to show user menu items (profile, wallet, logout)
 * @param customActions Optional slot for screen-specific actions (shown on desktop, hidden in mobile menu)
 */
@Composable
fun ResponsiveHeader(
    title: String,
    showBackButton: Boolean = false,
    onBackClick: (() -> Unit)? = null,
    showUserMenu: Boolean = true,
    customActions: @Composable (() -> Unit)? = null
) {
    val windowWidth = rememberWindowWidth()
    val isMobile = windowWidth < 768 // Mobile breakpoint
    val colors = Theme.colors

    val scope = rememberCoroutineScope()
    val currentUser by SharedData.currentUser.collectAsState()
    val wallet by SharedData.wallet.collectAsState()
    val engagement by SharedData.engagement.collectAsState()

    var isMenuOpen by remember { mutableStateOf(false) }
    var showLogoutConfirmation by remember { mutableStateOf(false) }

    // Close menu when switching to desktop
    LaunchedEffect(isMobile) {
        if (!isMobile) {
            isMenuOpen = false
        }
    }

    // Logout confirmation dialog
    if (showLogoutConfirmation) {
        ConfirmationDialog(
            title = "Logout",
            message = "Are you sure you want to logout?",
            confirmText = "Logout",
            cancelText = "Cancel",
            confirmVariant = "danger",
            onConfirm = {
                showLogoutConfirmation = false
                scope.launch {
                    com.fantasytenis.web.di.DependencyContainer.logoutUseCase()
                    NavigationState.navigate(Route.Login)
                }
            },
            onCancel = { showLogoutConfirmation = false }
        )
    }

    Header({
        style {
            flexShrink(0)
            backgroundColor(colors.primary)
            padding(12.px, if (isMobile) 16.px else 24.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            position(Position.Relative)
        }
    }) {
        // Left side: Back button (optional) + Title
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
            }
        }) {
            if (showBackButton && onBackClick != null) {
                // Back button
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        width(40.px)
                        height(40.px)
                        borderRadius(8.px)
                        backgroundColor(rgba(255, 255, 255, 0.15))
                        cursor("pointer")
                    }
                    onClick { onBackClick() }
                }) {
                    Span({
                        style {
                            color(Colors.White)
                            fontSize(20.px)
                        }
                    }) {
                        Text("\u2190") // Left arrow
                    }
                }
            }

            H2({
                style {
                    color(Colors.White)
                    fontSize(if (isMobile) 18.px else 24.px)
                    fontWeight(700)
                    margin(0.px)
                }
            }) {
                Text(title)
            }
        }

        // Right side: Desktop actions or Mobile hamburger
        if (isMobile) {
            // Mobile: Hamburger menu button
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    width(44.px)
                    height(44.px)
                    borderRadius(8.px)
                    backgroundColor(rgba(255, 255, 255, 0.15))
                    cursor("pointer")
                }
                onClick { isMenuOpen = !isMenuOpen }
            }) {
                Span({
                    style {
                        color(Colors.White)
                        fontSize(24.px)
                        lineHeight("1")
                    }
                }) {
                    Text(if (isMenuOpen) "\u2715" else "\u2630") // X or hamburger icon
                }
            }

            // Mobile dropdown menu
            if (isMenuOpen) {
                MobileMenu(
                    currentUser = currentUser,
                    wallet = wallet,
                    engagement = engagement,
                    onClose = { isMenuOpen = false },
                    onLogout = { showLogoutConfirmation = true },
                    colors = colors
                )
            }
        } else {
            // Desktop: Full inline actions
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                }
            }) {
                if (showUserMenu) {
                    DesktopUserActions(
                        currentUser = currentUser,
                        wallet = wallet,
                        engagement = engagement,
                        onLogout = { showLogoutConfirmation = true }
                    )
                }

                customActions?.invoke()
            }
        }
    }
}

@Composable
private fun DesktopUserActions(
    currentUser: UserEntity?,
    wallet: WalletEntity?,
    engagement: EngagementEntity?,
    onLogout: () -> Unit
) {
    // User info with avatar
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
        }
    }) {
        currentUser?.let { user ->
            AvatarDisplay(
                avatarUrl = user.avatarUrl,
                initials = user.initials,
                size = AvatarSize.SMALL,
                onClick = { NavigationState.navigate(Route.Profile) }
            )

            Span({
                style {
                    color(Colors.White)
                    fontSize(14.px)
                    cursor("pointer")
                }
                onClick { NavigationState.navigate(Route.Profile) }
            }) {
                Text(user.displayName)
            }
        }

        // Streak badge
        engagement?.let { eng ->
            if (eng.currentStreak > 0) {
                StreakBadge(
                    streak = eng.currentStreak,
                    compact = true,
                    onClick = { NavigationState.navigate(Route.Profile) }
                )
            }
        }

        // Wallet balance
        wallet?.let { w ->
            Span({
                style {
                    backgroundColor(rgba(255, 255, 255, 0.15))
                    padding(6.px, 12.px)
                    borderRadius(16.px)
                    color(Colors.White)
                    fontSize(13.px)
                    fontWeight(600)
                    cursor("pointer")
                }
                onClick { NavigationState.navigate(Route.Wallet) }
            }) {
                Text("$${w.totalBalance.asDynamic().toFixed(2)}")
            }
        }
    }

    // Admin link (only for admins)
    currentUser?.let { user ->
        if (user.isAdmin) {
            Span({
                style {
                    backgroundColor(rgba(255, 255, 255, 0.15))
                    padding(6.px, 12.px)
                    borderRadius(16.px)
                    color(Colors.White)
                    fontSize(13.px)
                    fontWeight(600)
                    cursor("pointer")
                }
                onClick { NavigationState.navigate(Route.Admin) }
            }) {
                Text("Admin")
            }
        }
    }

    // Notification Center
    NotificationCenter()

    // Theme toggle
    ThemeToggleIcon()

    // Logout button
    Button(
        text = "Logout",
        onClick = onLogout,
        variant = "secondary"
    )
}

@Composable
private fun MobileMenu(
    currentUser: UserEntity?,
    wallet: WalletEntity?,
    engagement: EngagementEntity?,
    onClose: () -> Unit,
    onLogout: () -> Unit,
    colors: com.fantasytenis.web.theme.ThemeColors
) {
    // Overlay to close menu when clicking outside
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            property("z-index", "999")
        }
        onClick { onClose() }
    })

    // Dropdown menu
    Div({
        style {
            position(Position.Absolute)
            top(100.percent)
            right(0.px)
            backgroundColor(colors.surface)
            borderRadius(12.px)
            property("box-shadow", "0 4px 20px rgba(0, 0, 0, 0.15)")
            minWidth(250.px)
            property("z-index", "1000")
            overflow("hidden")
            property("animation", "fadeIn 0.2s ease-out")
        }
    }) {
        // User profile section with streak
        currentUser?.let { user ->
            Div({
                style {
                    padding(16.px)
                    property("border-bottom", "1px solid ${colors.border}")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                    cursor("pointer")
                }
                onClick {
                    onClose()
                    NavigationState.navigate(Route.Profile)
                }
            }) {
                AvatarDisplay(
                    avatarUrl = user.avatarUrl,
                    initials = user.initials,
                    size = AvatarSize.MEDIUM
                )
                Div({
                    style {
                        flex(1)
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(8.px)
                        }
                    }) {
                        Span({
                            style {
                                fontWeight(600)
                                color(colors.textPrimary)
                                fontSize(15.px)
                            }
                        }) {
                            Text(user.displayName)
                        }
                        // Streak badge in mobile menu
                        engagement?.let { eng ->
                            if (eng.currentStreak > 0) {
                                StreakBadge(streak = eng.currentStreak, compact = true)
                            }
                        }
                    }
                    Div({
                        style {
                            fontSize(13.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text("View Profile")
                    }
                }
            }
        }

        // Wallet balance
        wallet?.let { w ->
            MobileMenuItem(
                icon = "\uD83D\uDCB0", // Money bag
                label = "Wallet",
                value = "$${w.totalBalance.asDynamic().toFixed(2)}",
                colors = colors,
                onClick = {
                    onClose()
                    NavigationState.navigate(Route.Wallet)
                }
            )
        }

        // Notifications
        val unreadCount by SharedData.unreadNotificationCount.collectAsState()
        MobileMenuItem(
            icon = "\uD83D\uDD14", // Bell
            label = "Notifications",
            value = if (unreadCount > 0) unreadCount.toString() else null,
            colors = colors,
            onClick = {
                onClose()
                // TODO: Navigate to full notifications screen when implemented
                // For now, just close the menu and the user can use desktop view
            }
        )

        // Admin link (if admin)
        currentUser?.let { user ->
            if (user.isAdmin) {
                MobileMenuItem(
                    icon = "\u2699\uFE0F", // Gear
                    label = "Admin Panel",
                    colors = colors,
                    onClick = {
                        onClose()
                        NavigationState.navigate(Route.Admin)
                    }
                )
            }
        }

        // Theme toggle
        Div({
            style {
                padding(14.px, 16.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                cursor("pointer")
            }
            onClick {
                com.fantasytenis.web.theme.ThemeState.toggleTheme()
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                Span({
                    style { fontSize(18.px) }
                }) {
                    Text("\uD83C\uDF19") // Moon
                }
                Span({
                    style {
                        color(colors.textPrimary)
                        fontSize(15.px)
                    }
                }) {
                    Text("Dark Mode")
                }
            }
            ThemeToggleIcon()
        }

        // Logout
        Div({
            style {
                padding(14.px, 16.px)
                property("border-top", "1px solid ${colors.border}")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
                cursor("pointer")
                color(Colors.Error)
            }
            onClick {
                onClose()
                onLogout()
            }
        }) {
            Span({
                style { fontSize(18.px) }
            }) {
                Text("\uD83D\uDEAA") // Door
            }
            Span({
                style {
                    fontSize(15.px)
                    fontWeight(500)
                }
            }) {
                Text("Logout")
            }
        }
    }
}

@Composable
private fun MobileMenuItem(
    icon: String,
    label: String,
    value: String? = null,
    colors: com.fantasytenis.web.theme.ThemeColors,
    onClick: () -> Unit
) {
    Div({
        style {
            padding(14.px, 16.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
            cursor("pointer")
        }
        onClick { onClick() }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
            }
        }) {
            Span({
                style { fontSize(18.px) }
            }) {
                Text(icon)
            }
            Span({
                style {
                    color(colors.textPrimary)
                    fontSize(15.px)
                }
            }) {
                Text(label)
            }
        }
        value?.let { v ->
            Span({
                style {
                    color(colors.primary)
                    fontSize(14.px)
                    fontWeight(600)
                }
            }) {
                Text(v)
            }
        }
    }
}
